/**
 * One write at a time per DOCUMENT, with what arrives meanwhile folded into
 * the next one — and a transient failure retried before anyone hears of it.
 *
 * Why: an operation write is a transaction that reads the whole document and
 * commits the touched arrays. Two of them in flight on the same document
 * contend — the second's read is stale when the first commits, and the SDK
 * re-reads the whole document (94 KB median for a structure, 862 KB for the
 * largest room) and tries again. A burst of Position-arrow presses is
 * exactly that. Serialising per document removes this tab's contention with
 * itself; another client's write still forces a retry, which is correct.
 *
 * Shape: the first item runs at once (a lone write pays no extra latency);
 * items that arrive while one is in flight wait, and when it finishes they
 * run as ONE batch, in arrival order, and every waiter of that batch gets
 * the batch's result or its error. Composing the items is the runner's job
 * — op sets must be applied in order through `applyOpeningOps`, never merged
 * key by key, or a remove followed by an add of the same id loses its
 * meaning.
 *
 * Retry: a transaction has no offline queue in the SDK — it fails with
 * `unavailable` the moment the connection is gone, where a plain `updateDoc`
 * would wait. So a TRANSIENT failure (the connection, a deadline, the
 * backend shedding load — `isTransientWriteError`) is retried here, on the
 * same batch, with backoff while online and for as long as it takes while
 * the browser says it is offline (the `online` event ends the wait at once).
 * Only once the attempts are spent does the batch reject, and the store
 * withdraws the optimistic value — a tunnel is a delayed edit, not a lost
 * one. Anything else (a permission, a validation, contention the SDK gave
 * up on) rejects at once, as before.
 *
 * Every item the queue holds — in flight, waiting for its batch, waiting out
 * a retry — is counted app-wide (`pendingWrites.ts`) for the "not saved"
 * indicator.
 *
 * Generic over the item and result so the structures session and the rooms
 * repository use the same helper over their own documents.
 */
import { trackPendingWrites } from './pendingWrites';

export interface WriteQueue<T, R> {
  /** Resolve with the result of the batch this item ran in. */
  enqueue(item: T): Promise<R>;
  /** True while a batch is in flight (a retry's wait included). */
  busy(): boolean;
  /** Items waiting for the next batch (for tests and diagnostics). */
  waiting(): number;
}

export interface RetryPolicy {
  /** Attempts while ONLINE before the batch rejects (the first run included). */
  attempts: number;
  /** Backoff before the second attempt; doubles each time, up to `maxDelayMs`. */
  baseDelayMs: number;
  maxDelayMs: number;
  isTransient: (error: unknown) => boolean;
}

interface Waiter<R> {
  resolve: (r: R) => void;
  reject: (e: unknown) => void;
}

const TRANSIENT_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'resource-exhausted',
]);

/**
 * A failure worth retrying: the SDK's `unavailable` (the connection, which is
 * also what an offline transaction reports), a deadline, or the backend
 * shedding load. Not `aborted`: that is contention the SDK already retried
 * five times and gave up on, and it rejects at once like a permission. The
 * SDK's `FirebaseError` carries `code`; the message is read too, for an
 * error wrapped on the way up.
 */
export function isTransientWriteError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  const codeStr =
    typeof code === 'string' ? code.replace(/^firestore\//, '') : '';
  if (TRANSIENT_CODES.has(codeStr)) return true;
  return (
    typeof message === 'string' &&
    /\b(unavailable|offline|deadline|network)\b/i.test(message)
  );
}

export const DEFAULT_RETRY: RetryPolicy = {
  attempts: 5,
  baseDelayMs: 1000,
  maxDelayMs: 8000,
  isTransient: isTransientWriteError,
};

const browserOnline = () =>
  typeof navigator === 'undefined' || navigator.onLine !== false;

/** How often an offline wait re-reads `navigator.onLine` by itself. */
export const OFFLINE_POLL_MS = 2000;

/**
 * Wait `delayMs`, or until the browser comes back online, whichever is
 * first. With no delay (offline) the wait ends on the `online` event — or
 * on a periodic re-read of `navigator.onLine`, the backstop for a WebView
 * whose `online` event cannot be depended on (and for an event that fired
 * before this wait began).
 */
function waitBeforeRetry(delayMs: number | null): Promise<void> {
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    const hasWindow =
      typeof window !== 'undefined' && !!window.addEventListener;
    const done = () => {
      if (timer) clearTimeout(timer);
      if (poll) clearInterval(poll);
      if (hasWindow) window.removeEventListener('online', done);
      resolve();
    };
    if (hasWindow) window.addEventListener('online', done);
    if (delayMs !== null) {
      timer = setTimeout(done, delayMs);
    } else {
      poll = setInterval(() => {
        if (browserOnline()) done();
      }, OFFLINE_POLL_MS);
    }
  });
}

/**
 * Run `run`, retrying a transient failure as `retry` says: while the browser
 * is online, up to `retry.attempts` tries with a doubling backoff; while it
 * is offline, no attempt is spent and the wait ends when it is back. The
 * last failure, or any failure that is not transient, rejects.
 *
 * The write queue retries each batch through this, and the access gate
 * (`components/PrivateRoute.js`) its first read: a read that never reached
 * the server says nothing about the account, so it is retried, not read as
 * "no access".
 */
export async function runWithRetry<R>(
  run: () => Promise<R>,
  retry: RetryPolicy = DEFAULT_RETRY
): Promise<R> {
  let onlineAttempts = 0;
  let delay = retry.baseDelayMs;
  for (;;) {
    try {
      return await run();
    } catch (error) {
      if (!retry.isTransient(error)) throw error;
      // Decided on the browser's state NOW, never on what it was when the
      // attempt began: an attempt started offline can take seconds to
      // fail, and if the connection is back by then the failure must
      // spend an attempt and back off — waiting for an `online` event
      // that already fired would hang the batch until the next toggle.
      if (browserOnline()) {
        onlineAttempts += 1;
        if (onlineAttempts >= retry.attempts) throw error;
        await waitBeforeRetry(delay);
        delay = Math.min(delay * 2, retry.maxDelayMs);
      } else {
        // Offline: no attempt is spent, and no backoff — the `online`
        // event, or the poll behind it, is what ends the wait.
        await waitBeforeRetry(null);
      }
    }
  }
}

/**
 * `pick(result, index)` turns the batch's result into the one the item at
 * `index` (its position in the batch) resolves with — for a runner whose
 * result carries per-item parts, a removal count per mutation, say. Identity
 * by default: every waiter gets the batch's result.
 */
export function createWriteQueue<T, R, P = R>(
  run: (batch: T[]) => Promise<R>,
  pick: (result: R, index: number) => P = (result) => result as unknown as P,
  retry: RetryPolicy = DEFAULT_RETRY
): WriteQueue<T, P> {
  let inFlight = false;
  let pendingItems: T[] = [];
  let pendingWaiters: Waiter<P>[] = [];

  /** The batch, retried on a transient failure as the policy says. */
  const attempt = (batch: T[]): Promise<R> =>
    runWithRetry(() => run(batch), retry);

  const drain = async (batch: T[], waiters: Waiter<P>[]) => {
    inFlight = true;
    try {
      const result = await attempt(batch);
      waiters.forEach((w, i) => w.resolve(pick(result, i)));
    } catch (error) {
      waiters.forEach((w) => w.reject(error));
    } finally {
      trackPendingWrites(-batch.length);
      inFlight = false;
      if (pendingItems.length) {
        const nextBatch = pendingItems;
        const nextWaiters = pendingWaiters;
        pendingItems = [];
        pendingWaiters = [];
        void drain(nextBatch, nextWaiters);
      }
    }
  };

  return {
    enqueue(item) {
      return new Promise<P>((resolve, reject) => {
        trackPendingWrites(1);
        if (!inFlight) {
          void drain([item], [{ resolve, reject }]);
          return;
        }
        pendingItems.push(item);
        pendingWaiters.push({ resolve, reject });
      });
    },
    busy: () => inFlight,
    waiting: () => pendingItems.length,
  };
}
